// See https://github.internal.digitalocean.com/digitalocean/walrus/blob/master/README.md#deprecating for more information

const trackedWarnings = new Set<string>();

const warningStyle = "color: red; font-weight: bold; font-size: 14px;";

export function deprecationWarning(deadExport: string, alternativeExport: string, documentationUrl?: string): void {
  /**
   * Don't warn in production because this is not a helpful message to see in production.
   */
  if (process.env.NODE_ENV === "production" || !!process.env.CI) {
    return;
  }

  /**
   * Create a unique key for this warning.
   */
  const key = `${deadExport}-${alternativeExport}`;

  /**
   * If we've already displayed the message once, we don't need to display it again.
   */
  if (trackedWarnings.has(key)) {
    return;
  }

  trackedWarnings.add(key);

  let documentationMessage = "";

  if (documentationUrl) {
    documentationMessage = `\n\nSee http://walrus.internal.digitalocean.com/${documentationUrl} for more information.`;
  }

  console.groupCollapsed(
    `%c🦭🦭🦭 WALRUS DEPRECATION WARNING 🦭🦭🦭\n\n${deadExport} is deprecated. Please use ${alternativeExport} instead.${documentationMessage}`,
    warningStyle
  );
  console.groupEnd();
}

/**
 * Use a set here to keep track of when we've already emitted a big warning so that we don't
 * completely flood the console.
 */
const bigWarningLogTracker = new Set<string>();

export const bigWarning = (message: string, key?: string): void => {
  if (key && bigWarningLogTracker.has(key)) {
    return;
  }

  console.log(message, warningStyle);

  if (key) {
    bigWarningLogTracker.add(key);
  }
};

export const passthroughWarning = <T extends object>(deadExport: string, alternativeExport: string, obj: T): T => {
  if (process.env.NODE_ENV === "production") {
    return obj;
  }

  return new Proxy<T>(obj, {
    get(target, key, receiver) {
      deprecationWarning(deadExport, alternativeExport);
      return Reflect.get(target, key, receiver);
    },
  });
};
